'use client';

import type { StarterKitOptions } from '@tiptap/starter-kit';
import { Pencil } from 'lucide-react';

import { useCallback, useMemo, useState } from 'react';

import RichTextContent from '@/components/rich-text-editor/rich-text-content';
import RichTextEditor from '@/components/rich-text-editor/rich-text-editor';
import { RTE_EXTENSIONS } from '@/components/rich-text-editor/types/rich-text-editor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { toast } from '@/shared/components/toast';
import { ImageUploadDialog } from '@/shared/components/image-upload/image-upload-dialog';
import { WorkspaceNameDialog } from '@/shared/components/workspace-name-dialog';
import { COPY } from '@/shared/constants/copies';

import { getGradientForName } from '@/utils/avatar-utils';

import { useCareersEdit } from '../../hooks/use-careers-edit';
import { normalizeCareersDescription } from '../../utils/careers-description';

const CAREERS_DESCRIPTION_EXTENSIONS = [
	RTE_EXTENSIONS.STARTER_KIT,
	RTE_EXTENSIONS.LINK,
	RTE_EXTENSIONS.UNDERLINE,
	RTE_EXTENSIONS.BULLET_LIST,
	RTE_EXTENSIONS.LIST_ITEM,
	RTE_EXTENSIONS.LIST_KEYMAP,
	RTE_EXTENSIONS.PLACEHOLDER,
];

const CAREERS_STARTER_KIT_OPTIONS: Partial<StarterKitOptions> = {
	blockquote: false,
	code: false,
	codeBlock: false,
	horizontalRule: false,
	strike: false,
};

export function CareersHeader() {
	const {
		careersData,
		isEditMode,
		description,
		setDescription,
		setDescriptionFocused,
		handleNameSave,
		isNameSaving,
		handleImageUpload,
		handleImageRemove,
		maxDescriptionLength,
	} = useCareersEdit();

	const displayName = careersData?.pageName ?? '';
	const logoUrl = careersData?.pageLogo;
	const editorDescription = useMemo(
		() => normalizeCareersDescription(description),
		[description]
	);
	const displayDescription = useMemo(
		() => normalizeCareersDescription(careersData?.description),
		[careersData?.description]
	);

	const handleDescriptionFocus = useCallback(
		() => setDescriptionFocused(true),
		[setDescriptionFocused]
	);
	const handleDescriptionBlur = useCallback(
		() => setDescriptionFocused(false),
		[setDescriptionFocused]
	);
	const handleDescriptionLimitExceeded = useCallback(() => {
		toast({ title: COPY.careers.descriptionTrimmed, variant: 'destructive' });
	}, []);

	// --- Image upload dialog ---
	const [imageDialogOpen, setImageDialogOpen] = useState(false);

	// --- Name edit dialog ---
	const [nameModalOpen, setNameModalOpen] = useState(false);

	return (
		<>
			<div className="mb-8 flex flex-col sm:mb-10">
				{/* Avatar — 80×80, 24px below before title */}
				{isEditMode ? (
					<button
						className="group relative mb-6 h-20 w-20 cursor-pointer border-none outline-none"
						onClick={() => setImageDialogOpen(true)}
					>
						<Avatar className="h-20 w-20 rounded-sm">
							<AvatarImage src={logoUrl} />
							<AvatarFallback
								style={{ background: getGradientForName(displayName) }}
								className="rounded-sm"
							>
								{displayName?.slice(0, 1)?.toUpperCase()}
							</AvatarFallback>
						</Avatar>
						<div className="absolute inset-0 flex items-center justify-center rounded-sm bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
							<Pencil className="size-4 text-white" />
						</div>
					</button>
				) : (
					<Avatar className="mb-6 h-20 w-20 rounded-sm">
						<AvatarImage src={logoUrl} />
						<AvatarFallback
							style={{ background: getGradientForName(displayName) }}
							className="rounded-sm"
						>
							{displayName?.slice(0, 1)?.toUpperCase()}
						</AvatarFallback>
					</Avatar>
				)}

				{/* Name + description — 16px between title and description */}
				<div className="flex flex-col gap-4">
					<div className="flex items-center gap-0.5">
						<h1 className="text-left text-2xl font-semibold capitalize tracking-[-0.015em] sm:text-[28px]">
							{displayName}
						</h1>
						{isEditMode && (
							<button
								type="button"
								className="cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								onClick={() => setNameModalOpen(true)}
								aria-label="Edit name"
							>
								<Pencil className="size-3.5" />
							</button>
						)}
					</div>

					{/* Description */}
					{isEditMode ? (
						<div className="-mx-2 flex flex-col rounded-md px-2">
							<RichTextEditor
								description={editorDescription}
								onChangeDescription={setDescription}
								editMode
								placeholder="Add a short description..."
								allowedExtensions={CAREERS_DESCRIPTION_EXTENSIONS}
								starterKitOptions={CAREERS_STARTER_KIT_OPTIONS}
								contentClassName="min-h-0 text-sm leading-5 text-muted-foreground"
								hideToolbar
								hideFocusUnderline
								stopOnChangeDebounce
								maxHtmlLengthOnBlur={maxDescriptionLength}
								onCharacterLimitExceeded={handleDescriptionLimitExceeded}
								onFocus={handleDescriptionFocus}
								onBlur={handleDescriptionBlur}
							/>
						</div>
					) : (
						displayDescription && (
							<RichTextContent
								html={displayDescription}
								className="break-words text-sm leading-relaxed text-muted-foreground sm:text-[15px]"
							/>
						)
					)}
				</div>
			</div>

			{/* Name edit modal — shared component */}
			<WorkspaceNameDialog
				open={nameModalOpen}
				onOpenChange={setNameModalOpen}
				currentName={displayName}
				onSave={handleNameSave}
				isSaving={isNameSaving}
			/>

			{/* Image upload modal */}
			<ImageUploadDialog
				open={imageDialogOpen}
				onOpenChange={setImageDialogOpen}
				onImageUpload={handleImageUpload}
				currentImageUrl={logoUrl}
				onImageRemove={handleImageRemove}
				title="Update workspace logo"
			/>
		</>
	);
}
